import { Hourglass } from 'lucide-react';
import { Carte, MessageErreur, Pastille } from '../ui/Primitives';
import { useToast } from '../ui/useToast';
import { ErreurApi } from '../../lib/api';
import { horodatageReunion } from '../../lib/emargement-affichage';
import { useModificationsDuDossier, useRetirerDemande } from '../../lib/useModificationsSensibles';
import { ListeChangements } from '../modifications-sensibles/ElementsDemande';

/**
 * Les modifications sensibles qui attendent la direction sur ce dossier
 * (TF-437) : identité, présence d'une feuille clôturée, note publiée. Rien
 * n'est modifié avant sa décision ; la carte le dit à qui ouvre le dossier, et
 * son auteur y retire sa demande. Sans demande, elle ne s'affiche pas.
 */
export function CarteModificationsEnAttente({ apprenantId }: { apprenantId: string }): JSX.Element | null {
  const { data } = useModificationsDuDossier(apprenantId);
  const retirer = useRetirerDemande();
  const toast = useToast();
  if (!data || data.enAttente.length === 0) return null;

  return (
    <Carte
      titre="Modifications en attente de la direction"
      actions={<Pastille texte={String(data.enAttente.length)} ton="warning" />}
    >
      <ul className="divide-y divide-gray-100">
        {data.enAttente.map((demande) => (
          <li key={demande.id} className="space-y-2 px-4 py-3 sm:px-6">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="flex items-center gap-1.5 text-sm font-semibold text-codialis-navy">
                  <Hourglass className="h-4 w-4 shrink-0 text-amber-600" aria-hidden />
                  {demande.typeLibelle}
                </p>
                <p className="break-words text-xs text-codialis-gray">{demande.objet}</p>
              </div>
              {demande.refusRetrait === null && (
                <button
                  type="button"
                  disabled={retirer.isPending}
                  onClick={() => retirer.mutate(demande.id, { onSuccess: (resultat) => toast(resultat.message) })}
                  className="inline-flex min-h-[36px] items-center rounded-lg px-2 text-xs font-semibold text-codialis-sky hover:underline disabled:opacity-50 [@media(pointer:coarse)]:min-h-[44px]"
                >
                  Retirer ma demande
                </button>
              )}
            </div>
            <ListeChangements changements={demande.changements} />
            <p className="whitespace-pre-line break-words text-xs text-codialis-gray">
              Demandée le {horodatageReunion(demande.demandeLe)}
              {demande.demandePar ? ` par ${demande.demandePar}` : ''} · « {demande.motif} »
            </p>
          </li>
        ))}
      </ul>
      {retirer.error && (
        <div className="px-4 pb-3 sm:px-6">
          <MessageErreur
            message={retirer.error instanceof ErreurApi ? retirer.error.message : 'La demande n’a pas pu être retirée.'}
          />
        </div>
      )}
    </Carte>
  );
}
